import { isEqual, isNil, pick } from 'lodash';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { API } from '@shared/types/api';
import { omitTypeName } from '@shared/util/omitTypeName';
import { JsonStorage } from '@shared/util/storage/JsonStorage';
import { useViewTemplateActions, useViewTemplateState } from './ViewTemplateProvider';

const VIEW_TEMPLATE_TTL = 1000 * 60 * 60 * 24; // 1 day

// only saving portions of the view template
type EditableViewTemplate = Pick<
  API.ViewTemplateFragment,
  'componentsViewOptions' | 'defaultHiddenLabels' | 'collectionCriteria'
>;

interface PreserveViewTemplateProps {
  setIsModified: Dispatch<SetStateAction<boolean>>;
}

export function PreserveViewTemplate({ setIsModified }: PreserveViewTemplateProps) {
  const { viewTemplate, savedViewTemplate } = useViewTemplateState();
  const { setViewTemplate } = useViewTemplateActions();
  const storageKey = `viewTemplate-${viewTemplate.id}`;

  useEffect(() => {
    const edited = getEditableViewTemplate(viewTemplate);
    const saved = getEditableViewTemplate(savedViewTemplate);
    const isChanged = !isEqual(edited, saved);
    setIsModified(isChanged);
    if (isChanged) {
      // while changing views, can sometimes get a false positive, so delay saving
      const timer = setTimeout(() => {
        JsonStorage.set(storageKey, edited, VIEW_TEMPLATE_TTL);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      JsonStorage.remove(storageKey);
    }
  }, [viewTemplate, savedViewTemplate, storageKey, setIsModified]);

  useEffect(() => {
    // load viewTemplate from storage
    async function loadStoredViewTemplate() {
      const storedViewTemplate = await JsonStorage.get<EditableViewTemplate>(storageKey);
      if (!isNil(storedViewTemplate)) {
        setViewTemplate(prev => ({ ...prev, ...storedViewTemplate }));
      }
    }
    loadStoredViewTemplate();
  }, [storageKey, setViewTemplate]);

  return null;
}

export function getEditableViewTemplate(
  viewTemplate: API.ViewTemplateFragment,
): EditableViewTemplate {
  const viewTemplateWithoutTypeName = omitTypeName(viewTemplate);
  return pick(
    viewTemplateWithoutTypeName,
    'name',
    'subtype',
    'calendarDateComponentId',
    'checklistTagComponentId',
    'componentsViewOptions',
    'defaultHiddenLabels',
    'collectionCriteria',
  );
}
